/**
 * The primary colour is a company setting: every user of a company sees the
 * company's brand colour, and signed out (the login page) it is the plain default.
 *
 * It used to be a per-browser pick as well, kept in localStorage and applied inline
 * on <html>, where it beat the company colour — so two users of one company, or one
 * user on two machines, saw different colours. That pick is gone; a value left over
 * from it is cleared on load so it stops overriding the company.
 */

const LEGACY_STORAGE_KEY = 'lognai-primary-color';
const LEGACY_INLINE_VARS = [
  '--primary',
  '--ring',
  '--sidebar-primary',
  '--sidebar-accent',
  '--sidebar-ring',
  '--primary-subtle',
] as const;

try {
  window.localStorage.removeItem(LEGACY_STORAGE_KEY);
} catch {}
for (const v of LEGACY_INLINE_VARS) document.documentElement.style.removeProperty(v);

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

/** Returns a normalized `#rrggbb` string, or null if invalid. */
export function normalizeHex(input: string): string | null {
  const v = input.trim();
  if (!HEX_RE.test(v)) return null;
  return (v.startsWith('#') ? v : `#${v}`).toLowerCase();
}

/**
 * It goes in a <style> element rather than inline on <html> because it carries a
 * separate dark-theme value, which an inline property cannot. Being unlayered, its
 * rules beat the design system's `@layer base` tokens; the dark rule comes second so
 * it wins on <html data-theme="dark">.
 *
 * `--accent` is deliberately not set. It is the brand violet, a separate role
 * reserved for AI surfaces and secondary emphasis; `--sidebar-accent` is set,
 * because that one genuinely is the nav's active-highlight tint.
 */
export interface CompanyPrimaryColor {
  light: string;
  dark?: string | null;
}
const COMPANY_STYLE_ID = 'lognai-company-primary';

function companyCss({ light, dark }: CompanyPrimaryColor): string {
  const block = (c: string, sidebar: boolean) =>
    [
      `--primary:${c}`,
      `--ring:${c}`,
      `--primary-subtle:${c}1f`,
      ...(sidebar ? [`--sidebar-primary:${c}`, `--sidebar-accent:${c}`, `--sidebar-ring:${c}`] : []),
    ].join(';');
  // The dark sidebar keeps the light-theme brand, as the theme defaults do.
  return `:root{${block(light, true)}}` + (dark ? `[data-theme="dark"]{${block(dark, false)}}` : '');
}

export function setCompanyPrimaryColor(color: CompanyPrimaryColor | null) {
  const light = color ? normalizeHex(color.light) : null;
  const dark = color?.dark ? normalizeHex(color.dark) : null;
  let el = document.getElementById(COMPANY_STYLE_ID) as HTMLStyleElement | null;
  if (!light) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = COMPANY_STYLE_ID;
    document.head.appendChild(el);
  }
  const css = companyCss({ light, dark });
  if (el.textContent !== css) el.textContent = css;
}
